import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useInView } from "react-intersection-observer";
import { Box, TextField, Typography, Divider, InputAdornment, CircularProgress } from "@mui/material";

import { useInfiniteSearchUsers } from "../../../User/api/queries/useInfiniteSearchUsers";
import { SearchGroupMembersFieldIcon } from "../../assets/icons/groups-icons";
import UserCard from "../../../User/components/UserCard";
import { Controller, useFormContext } from "react-hook-form";
import { GroupFormData } from "../../types/group.types";

export const GroupMembers = () => {
  const { control } = useFormContext<GroupFormData>();
  const { t } = useTranslation("groups");
  const [searchValue, setSearchValue] = useState<string>("");

  const {
    data: users,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteSearchUsers({
    limit: 12,
    offset: 0,
    name: searchValue,
  });

  const { ref, inView } = useInView();

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, fetchNextPage, isFetchingNextPage]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        width: "100%",
      }}
    >
      <Typography
        sx={{
          color: "var(--Dark---1000, #111)",
          fontFamily: "Montserrat",
          fontSize: "16px",
          fontStyle: "normal",
          fontWeight: 700,
          lineHeight: "normal",
        }}
      >
        {t("setGroupMembers")}
      </Typography>
      <TextField
        placeholder={t("searchByName")}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchGroupMembersFieldIcon />
            </InputAdornment>
          ),
        }}
        helperText={t("selectGroupMembers")}
        onChange={(e) => setSearchValue(e.target.value)}
      />

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          overflowY: "auto",
          maxHeight: "896px",
          px: "16px",
          borderBottom: "1px solid var(--Dark---200, #E0E0E0)",
        }}
      >
        <Controller
          name="users"
          control={control}
          render={({ field }) => (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              {users?.map((user, index) => (
                <>
                  <UserCard
                    key={user.id}
                    user={user}
                    variant="list"
                    showCheckbox
                    isSelected={field.value?.includes(user.id)}
                    onSelect={() => {
                      const currentIds = field.value || [];
                      const newIds = currentIds.includes(user.id)
                        ? currentIds.filter((id) => id !== user.id)
                        : [...currentIds, user.id];
                      field.onChange(newIds);
                    }}
                  />
                  {index !== users.length - 1 && (
                    <Divider orientation="horizontal" sx={{ border: "1px solid var(--Dark---200, #E0E0E0)" }} />
                  )}
                </>
              ))}
            </Box>
          )}
        />
        {(isFetchingNextPage || isLoading) && (
          <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
            <CircularProgress />
          </Box>
        )}
        <Box ref={ref} sx={{ height: "20px" }} />
      </Box>
    </Box>
  );
};
